import { useMemo, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { useUser } from "@components/user-context";
import { SecondaryButton, TertiaryButton } from "@components/v2/buttons";
import { NbSet } from "@features/mmm/utils/nb-set";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type FetchClientSettingsForCycle,
  type FetchClientSettingsForCycleVariables,
} from "@north-beam/nb-client-side";
import { type MMMClientCycleBase } from "@north-beam/nb-common";
import { formatCurrency } from "@north-beam/nb-common/formatting";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { CLIENT_SETTINGS_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useClientActiveSettings } from "@pages/mmm/hooks/use-client-active-settings";
import { useClientCycles } from "@pages/mmm/hooks/use-client-cycles";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { ClientSettingNetworkErrorModal } from "./client-setting-network-error-modal";
import { useUpsertClientSetting } from "./hooks/use-upsert-client-setting";
import { type ClientSetting } from "./types";

const DATE_RANGE_FORMAT = "YYYY-MM-DD";

type DateRangeSettings = Record<string, ClientSetting[]>;
export enum ButtonType {
  SECONDARY,
  TERTIARY,
}
type ButtonOptions = {
  text: string;
  type: ButtonType;
  icon?: string;
};

export function HistoricalClientSettingChooser({
  buttonOptions = {
    text: "Copy setting from previous media plan",
    type: ButtonType.TERTIARY,
    icon: "clock-rotate-left",
  },
}: {
  buttonOptions?: ButtonOptions;
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const [clientCycles] = useClientCycles();
  const { selectedCycle } = useSelectedCycle();
  const [clientActiveSettings] = useClientActiveSettings();

  const [showNetworkErrorModal, setShowNetworkErrorModal] = useState(false);

  const { loadData: fetchClientSettingsForCycle } = useNorthbeamLazyQuery<
    FetchClientSettingsForCycle,
    FetchClientSettingsForCycleVariables
  >(CLIENT_SETTINGS_FOR_CYCLE_QUERY, {
    onCompleted: (data) => {
      // NOTE: For some reason Nexus enum is incompatible with
      // TS enum types, temp fix w/ as unknown...
      const clientSettings = data.me.mmm.cycle
        .clientSettings as unknown as ClientSetting[];
      setSettingsForDateRanges((prevState) => ({
        ...prevState,
        [data.me.mmm.cycle.id]: clientSettings,
      }));
    },
  });

  const {
    upsertClientSetting,
    loading: loadingUpsertClientSetting,
    transformClientSettingToPayload,
  } = useUpsertClientSetting();

  const [settingsForDateRanges, setSettingsForDateRanges] =
    useState<DateRangeSettings>({});
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleSelectClientSetting = async (clientSetting: ClientSetting) => {
    trackMMMEvent({
      component:
        ExploreBudgetsTrackedComponent.COPY_HISTORICAL_CLIENT_SETTING_SELECTOR,
      action: "onSelect",
    });

    const wasSuccessful = await upsertClientSetting(
      transformClientSettingToPayload(clientSetting, selectedCycle),
    );
    if (!wasSuccessful) {
      setShowNetworkErrorModal(true);
    }
  };

  const cycles = useMemo(
    () =>
      [...clientCycles]
        .sort((c1, c2) => {
          const start1 = moment(c1.date_range.start_date, DATE_RANGE_FORMAT);
          const start2 = moment(c2.date_range.start_date, DATE_RANGE_FORMAT);
          return start1.isBefore(start2) ? -1 : 1;
        })
        .filter((cycle) => cycle.id !== selectedCycle.id),
    [clientCycles, selectedCycle],
  );

  const isButtonDisabled = useMemo(() => {
    const cycleDateRangeIds = new NbSet(
      cycles.map((cycle) => cycle.date_range.id),
    );
    const activeSettingDateRangeIds = new NbSet(
      clientActiveSettings.map(
        (setting) => setting.client_setting_date_range.id,
      ),
    );
    return cycleDateRangeIds.intersection(activeSettingDateRangeIds).size === 0;
  }, [cycles, clientActiveSettings]);

  const loadClientSettingsForCycle = (cycleId: string) => {
    if (settingsForDateRanges[cycleId]) {
      return;
    }
    fetchClientSettingsForCycle({
      variables: { id: cycleId },
    });
  };

  // const loadClientSettingsForDateRange = (dateRangeId: number) => {
  //   if (settingsForDateRanges[dateRangeId] !== undefined) {
  //     return;
  //   }
  //   const clientSettings =
  //     dateRangeClientSettings.find(
  //       (dateRangeData) => dateRangeData.date_range.id === dateRangeId,
  //     )?.date_range.client_settings || [];
  //   setSettingsForDateRanges((prevState) => ({
  //     ...prevState,
  //     [dateRangeId]: clientSettings,
  //   }));
  // };

  const ButtonComponent =
    buttonOptions.type === ButtonType.SECONDARY
      ? SecondaryButton
      : TertiaryButton;

  const { refs, floatingStyles, context } = useFloating({
    open: isPopoverOpen,
    onOpenChange: setIsPopoverOpen,
    transform: false,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift(), offset(2)],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div>
      <div>
        <ButtonComponent
          className="uppercase font-bold flex items-center gap-2"
          disabled={isButtonDisabled}
          loading={loadingUpsertClientSetting}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          {buttonOptions.icon && (
            <i className={`fa-regular fa-${buttonOptions.icon} text-sm`} />
          )}
          {buttonOptions.text}
        </ButtonComponent>
        <FloatingPortal>
          <AnimatePresence>
            {isPopoverOpen && (
              <motion.div
                transition={{ ease: "easeOut", duration: 0.2 }}
                initial={{ opacity: 0, translateY: 0 }}
                animate={{ opacity: 1, translateY: 0 }}
                exit={{ opacity: 0, translateY: 0 }}
                ref={refs.setFloating}
                style={floatingStyles}
                className="z-10 bg-v2-primary rounded-lg elevation-1 text-sm max-h-80 overflow-auto"
                {...getFloatingProps()}
              >
                {cycles.map((cycle) => (
                  <ClientDateRangeOption
                    key={cycle.id}
                    cycle={cycle}
                    clientSettings={settingsForDateRanges[cycle.id]}
                    onSelectSetting={handleSelectClientSetting}
                    closeMenu={() => setIsPopoverOpen(false)}
                    onDateRangeSelected={() =>
                      loadClientSettingsForCycle(cycle.id)
                    }
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </div>
      <ClientSettingNetworkErrorModal
        isOpen={showNetworkErrorModal}
        closeModal={() => setShowNetworkErrorModal(false)}
        titleText="There was a problem copying your setting"
      />
    </div>
  );
}

type ClientDateRangeOptionProps = {
  cycle: MMMClientCycleBase;
  onSelectSetting: (clientSetting: ClientSetting) => void;
  clientSettings?: ClientSetting[];
  closeMenu: () => void;
  onDateRangeSelected: (dateRangeId: string) => void;
};

function ClientDateRangeOption({
  cycle,
  onSelectSetting,
  clientSettings,
  closeMenu,
  onDateRangeSelected,
}: ClientDateRangeOptionProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isPopoverOpen,
    onOpenChange: (open) => {
      if (open) {
        onDateRangeSelected(cycle.id);
      }
      setIsPopoverOpen(open);
    },
    transform: false,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift()],
  });

  const hover = useHover(context, {
    handleClose: safePolygon({ blockPointerEvents: true }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const formatDate = (date: string) => {
    return moment(date, "YYYY-MM-DD").format("MMM DD, YYYY");
  };

  const renderOverlay = () => {
    if (!clientSettings) {
      return (
        <div className="flex items-center ml-2 h-10 w-20">
          <img
            src={salesPlatformColumnLoading}
            alt="Loading table"
            style={{
              height: "2rem",
            }}
          />
        </div>
      );
    }

    if (clientSettings.length === 0) {
      return (
        <div className="w-[15rem] h-[5rem] flex items-center justify-center font-semibold">
          No client settings available
        </div>
      );
    }
    return (
      <>
        {clientSettings.map((clientSetting) => (
          <HistoricalClientSettingSelection
            key={clientSetting.id}
            clientSetting={clientSetting}
            onSelectSetting={onSelectSetting}
            closeMenu={() => {
              setIsPopoverOpen(false);
              closeMenu();
            }}
          />
        ))}
      </>
    );
  };

  return (
    <div>
      <div
        className="text-v2-primary flex justify-between items-center w-60 p-2 hover:bg-v2-secondary hover:font-semibold"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div>
          <div>
            {formatDate(cycle.date_range.start_date)} -{" "}
            {formatDate(cycle.date_range.end_date)}
          </div>
          {cycle.name && (
            <div className="text-2xs text-v2-secondary mt-0.5">
              {cycle.name}
            </div>
          )}
        </div>
        <i className="fa-regular fa-chevron-right text-sm text-cosmic"></i>
      </div>
      <FloatingPortal>
        <AnimatePresence>
          {isPopoverOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              ref={refs.setFloating}
              style={floatingStyles}
              className="z-10 bg-v2-primary rounded-lg elevation-1 text-sm max-h-80 overflow-auto"
              {...getFloatingProps()}
            >
              {renderOverlay()}
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
}

function HistoricalClientSettingSelection({
  clientSetting,
  onSelectSetting,
  closeMenu,
}: {
  clientSetting: ClientSetting;
  onSelectSetting: (clientSetting: ClientSetting) => void;
  closeMenu: () => void;
}) {
  const { user } = useUser();
  const { kpi: clientSettingKpi, spendConstraints } =
    useClientSettingData(clientSetting);
  const formatMoney = (val: number) => {
    return formatCurrency(val, {
      precision: "approximate",
      code: user.currencies.currentCurrency,
    });
  };

  const handleSelection = () => {
    onSelectSetting(clientSetting);
    closeMenu();
  };

  return (
    <div
      className="w-80 h-full flex flex-col gap-2 p-4 cursor-pointer hover:bg-cosmic-4"
      onClick={handleSelection}
    >
      <span className="text-sm font-semibold">{clientSetting.name}</span>
      <div className="flex gap-2 items-center justify-start">
        <span className="text-gray text-xs">KPI:</span>
        <span className="text-cosmic text-xs">
          {clientSettingKpi?.friendly_name}
        </span>
      </div>
      <div className="flex gap-2 items-center justify-start">
        <span className="text-gray text-xs">Budget:</span>
        <span className="text-cosmic text-xs">
          {formatMoney(clientSetting.budget_low)} -{" "}
          {formatMoney(clientSetting.budget_high)}
        </span>
      </div>
      <div className="flex gap-2 items-center justify-start">
        <span className="text-gray text-xs">Constraints:</span>
        <span className="text-cosmic text-xs">{spendConstraints.length}</span>
      </div>
    </div>
  );
}
